import { cn } from '@/lib/utils';
import NoItems from '@/components/ui/NoItems.tsx';
import {
  ChangeRoute,
  ChangeRouteOptionVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import {
  RadioCard,
  RadioDot,
  RadioGroup,
} from '@/components/ui/radio-group.tsx';
import { SectionLabel } from '@/components/ui/Title.tsx';
import Chip, { ChipTone } from '@/components/ui/Chip.tsx';

const tagTone = (route: ChangeRoute, isChecked: boolean): ChipTone => {
  if (!isChecked) {
    return 'neutral';
  }
  return route === ChangeRoute.CLIENT_ACCEPTANCE ? 'accent' : 'error';
};

interface ChangeRouteChoiceProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  routes: ChangeRouteOptionVm[];
  value: ChangeRoute;
  onChange: (route: ChangeRoute) => void;
}

/**
 * Wybór trasy wprowadzenia paczki — do akceptacji zapisującego albo od razu.
 * Trasa steruje nie tylko żądaniem `resolve`, ale też blokiem dokumentów pod
 * spodem: bez kroku akceptacji nie ma komu ich podpisać.
 */
const ChangeRouteChoice = ({
  routes,
  value,
  onChange,
  className,
  ...props
}: ChangeRouteChoiceProps) => (
  <div className={className} {...props}>
    <SectionLabel>Jak paczka wchodzi w życie — wybierz jedno</SectionLabel>

    {routes.length === 0 ? (
      <NoItems
        className="mt-3"
        title="Brak tras wprowadzenia"
        description="Nie wiadomo, w jaki sposób paczka może wejść w życie."
      />
    ) : (
      <RadioGroup
        aria-label="Jak paczka wchodzi w życie"
        value={value}
        onValueChange={(next) => onChange(next as ChangeRoute)}
        className="@max-[700px]:grid-cols-1 mt-3 grid grid-cols-2 gap-3"
      >
        {routes.map((route) => {
          const isChecked = route.route === value;

          return (
            <RadioCard
              key={route.route}
              value={route.route}
              className={cn(
                'block w-full border border-neutral-200 bg-neutral-50 px-[17px] py-[15px]',
                'data-[state=unchecked]:hover:border-neutral-300',
                'data-[state=checked]:border-accent-600 data-[state=checked]:bg-basic-white data-[state=checked]:shadow-[0_0_0_2px_theme(colors.accent-200)]',
              )}
            >
              <span className="flex items-center gap-[11px]">
                <RadioDot />
                <span className="text-base font-bold text-ink">
                  {route.title}
                </span>
                <Chip
                  label={route.tag}
                  tone={tagTone(route.route, isChecked)}
                  fill="soft"
                  className="ml-auto shrink-0"
                />
              </span>
              <span className="mt-2 block text-pretty pl-[27px] text-sm leading-normal text-neutral-500">
                {route.description}
              </span>
            </RadioCard>
          );
        })}
      </RadioGroup>
    )}
  </div>
);

export default ChangeRouteChoice;
